import {formatDate} from '@/dayjs'
import {
    CreateTreatmentRequestValidationSchema,
    DiseaseBase,
    DiseaseExtended,
    TreatmentRequestSmall,
    TreatmentRequestExtended,
    TreatmentRequestsColumn,
    TreatmentRequestsSearchParams,
    TreatmentRequestScore
} from '@/features/treatment-requests/types.ts'
import {palette} from '@/theme/palette'
import {TreatmentRequestEligibilityStatus} from '@/types'
import {ObjectEntries, ObjectKeys, pickPropsFromObj} from '@/utilities/helpers'
import {TKey} from '@/translations/i18n.tsx'
import {useParsedSearchParams} from '@/hooks/useParsedSearchParams'

export const authorizationToLabel = {
    asl: 'commons:asl',
    hospital_doctor: 'treatment_requests:hospital_doctor'
} as const satisfies Record<NonNullable<TreatmentRequestSmall['authorization']>, TKey>

export const typeToLabel = {
    new_takeover: 'treatment_requests:new_assignment',
    treatment_resumption: 'treatment_requests:type_treatment_resumption',
    cycle_resumption: 'treatment_requests:type_cycle_resumption'
} as const satisfies Record<TreatmentRequestSmall['type'], TKey>

export const treatmentRequestStatusToLabel = {
    in_review: 'treatment_requests:status_in_review',
    waiting_list: 'treatment_requests:status_waiting_list',
    in_progress: 'treatment_requests:status_in_progress',
    accepted: 'treatment_requests:status_accepted',
    rejected: 'treatment_requests:status_rejected',
    deprecated: 'treatment_requests:status_deprecated'
} as const satisfies Record<TreatmentRequestSmall['status'], TKey>

export const rejectedReasonToLabel = {
    request_expired: 'treatment_requests:rejected_reason_request_expired',
    request_not_compatible: 'treatment_requests:rejected_reason_request_not_compatible',
    taken_over_by_another_center: 'treatment_requests:rejected_reason_taken_over_by_another_center',
    deceased: 'commons:patient_deceased',
    other: 'treatment_requests:rejected_reason_other_patient'
} as const satisfies Record<NonNullable<TreatmentRequestSmall['rejectedReason']>, TKey>

export const treatmentRequestSectorToLabel = {
    outpatient: 'commons:outpatient',
    domiciliary: 'commons:domiciliary'
} as const satisfies Record<NonNullable<TreatmentRequestSmall['sector']>, TKey>

export const diseasesGroupNameToLabel = {
    'I° Gruppo': 'treatment_requests:disease_group_1',
    'II° Gruppo': 'treatment_requests:disease_group_2',
    'III° Gruppo': 'treatment_requests:disease_group_3',
    'IV° Gruppo': 'treatment_requests:disease_group_4',
    'V° Gruppo': 'treatment_requests:disease_group_5',
    'VI° Gruppo': 'treatment_requests:disease_group_6'
} as const satisfies Record<DiseaseBase['name'], TKey>

export const treatmentRequestEligibilityStatusToLabel = {
    'patient-deceased': 'commons:patient_deceased',
    'patient-on-shared-waiting-list': 'treatment_requests:eligibility_patient_on_shared_waiting_list',
    'pri-in-progress': 'treatment_requests:eligibility_pri_in_progress',
    'request-already-exists': 'treatment_requests:eligibility_request_already_exists',
    'resident-outside-the-region': 'treatment_requests:eligibility_resident_outside_the_region'
} as const satisfies Record<TreatmentRequestEligibilityStatus, TKey>

export const treatmentRequestStatusToColors = {
    in_review: palette.light.purple['400'],
    waiting_list: palette.light.orange['500'],
    in_progress: palette.light.primary['500'],
    accepted: palette.light.green['500'],
    rejected: palette.light.red['500'],
    deprecated: palette.light.neutral['500']
} as const satisfies Record<TreatmentRequestSmall['status'], string>

export type Score = {
    label: string
    value: number
}

export const treatmentRequestScoresMap = {
    social_data: {
        label: 'treatment_requests:score_social_data',
        scores: [
            {label: '1', value: 1},
            {label: '2', value: 2}
        ]
    },
    subtype: {
        label: 'treatment_requests:score_subtype',
        scores: [
            {label: '1', value: 1},
            {label: '2', value: 2},
            {label: '3', value: 3}
        ]
    },
    comorbidity: {
        label: 'treatment_requests:score_comorbidity',
        scores: [
            {label: '1', value: 1},
            {label: '2', value: 2},
            {label: '3', value: 3}
        ]
    },
    objectives: {
        label: 'commons:objectives',
        scores: [
            {label: '1', value: 1},
            {label: '2', value: 2}
        ]
    },
    reporting: {
        label: 'treatment_requests:score_reporting',
        scores: [
            {label: '1', value: 1},
            {label: '2', value: 2}
        ]
    },
    acuteness: {
        label: 'treatment_requests:score_acuteness',
        scores: [
            {label: '1', value: 1},
            {label: '2', value: 2}
        ]
    },
    social_discomfort: {
        label: 'treatment_requests:score_social_discomfort',
        scores: [
            {label: '1', value: 1},
            {label: '2', value: 2},
            {label: '3', value: 3},
            {label: '4', value: 4}
        ]
    },
    previous_treatments: {
        label: 'treatment_requests:score_previous_treatments',
        scores: [
            {label: '1 (continuativo)', value: 1},
            {label: '2 (già più cicli)', value: 2},
            {label: '3 (già un ciclo)', value: 3},
            {label: '4 (mai)', value: 4}
        ]
    },
    severity_pathology: {
        label: 'treatment_requests:score_severity_pathology',
        scores: [
            {label: '1', value: 1},
            {label: '2', value: 2},
            {label: '3', value: 3},
            {label: '4', value: 4}
        ]
    },
    age_groups_pathology: {
        label: 'treatment_requests:score_age_groups_pathology',
        scores: [
            {label: '1', value: 1},
            {label: '2', value: 2},
            {label: '3', value: 3},
            {label: '4', value: 4}
        ]
    },
    neuromuscular_index: {
        label: 'treatment_requests:score_neuromuscular_index',
        scores: [
            {label: '0', value: 0},
            {label: '30', value: 30}
        ]
    }
} as const satisfies Record<Exclude<TreatmentRequestScore['type'], 'precocity'>, {label: TKey; scores: Score[]}>

export const adultsScoresMap = pickPropsFromObj(treatmentRequestScoresMap, [
    'social_data',
    'subtype',
    'comorbidity',
    'objectives',
    'reporting',
    'acuteness'
])

export const evolutionaryAgeScoresMap = pickPropsFromObj(treatmentRequestScoresMap, [
    'social_discomfort',
    'previous_treatments',
    'severity_pathology',
    'age_groups_pathology',
    'neuromuscular_index'
])

export const remapDiseaseHierarchy = (disease: TreatmentRequestExtended['disease']): DiseaseExtended[] => {
    const result: DiseaseExtended[] = []

    const buildArray = (node: TreatmentRequestExtended['disease']) => {
        if (!node) return

        const currentNode: DiseaseExtended = {
            id: node.id,
            name: node.name,
            parent: node.parent,
            serviceType: node.serviceType
        }

        result.push(currentNode)
        buildArray(node.parent)
    }

    buildArray(disease)
    return result
}

export const remapTreatmentRequestToDefaultValues = (treatmentRequest: TreatmentRequestExtended) => {
    const remappedTreatmentRequest = Object.fromEntries(
        ObjectEntries(treatmentRequest).map(entry => {
            if (!entry) {
                throw new Error('treatment request entry is falsy')
            }

            const [key, value] = entry

            switch (key) {
                case 'authorizationDate': {
                    return ['authorizationDate', value && new Date(value)]
                }
                case 'disease': {
                    return ['diseases', remapDiseaseHierarchy(value).reverse()]
                }
                case 'diseaseOnsetDate': {
                    return ['diseaseOnsetDate', value && new Date(value)]
                }
                default: {
                    return [key, value]
                }
            }
        })
    )

    return {
        ...remappedTreatmentRequest,
        authorizationToRequest: !treatmentRequest.authorizationDate,
        patient: {
            id: treatmentRequest.patientId,
            fullName: treatmentRequest.patientFullName
        },
        project: null // todo: replace this as soon as it is released from the backend
    }
}

export const remapTreatmentRequestToPayload = (
    formValues: NonNullable<Partial<CreateTreatmentRequestValidationSchema>>,
    skipNullish?: boolean
) => {
    return Object.fromEntries(
        ObjectEntries(formValues)
            .map(entry => {
                if (!entry) {
                    throw new Error('treatment request entry is falsy')
                }

                const [key, value] = entry

                switch (key) {
                    case 'authorizationDate': {
                        return ['authorizationDate', value && formatDate(value.toISOString(), 'YYYY-MM-DD')]
                    }
                    case 'diseases': {
                        return ['diseaseId', value?.at(-1)?.id]
                    }
                    case 'diseaseOnsetDate': {
                        return ['diseaseOnsetDate', value && formatDate(value.toISOString(), 'YYYY-MM-DD')]
                    }
                    case 'diagnosisCode': {
                        return ['diagnosisCodeId', value?.id ?? null]
                    }
                    case 'patient': {
                        return ['patientId', value?.id]
                    }
                    case 'scores': {
                        return ['scores', value?.filter(score => score.score != null)]
                    }
                    case 'notificationsRecipients': {
                        return ['notificationRecipientIds', value?.map(recipient => recipient.id)]
                    }
                    default: {
                        return [key, value]
                    }
                }
            })
            .filter(entry => {
                const [, value] = entry
                const isEmptyArray = Array.isArray(value) && value.length == 0
                const isEmptyObject = typeof value == 'object' && value != null && ObjectKeys(value).length == 0
                const shouldKeepValue = !!(value || typeof value == 'boolean')
                return !skipNullish || (shouldKeepValue && !isEmptyArray && !isEmptyObject)
            })
    )
}

export const makeDiseaseTreePath = (treatmentRequest: TreatmentRequestSmall): string[] => {
    const result = []
    let diseaseLevel = treatmentRequest.disease

    while (diseaseLevel) {
        result.push(diseaseLevel.name)
        diseaseLevel = diseaseLevel.parent
    }

    return result.reverse()
}

export function extractGroupsDiseaseTree(treatmentRequestDisease: TreatmentRequestExtended['disease']): {
    group: TreatmentRequestExtended['disease']
    subgroups: TreatmentRequestExtended['disease'][]
} {
    const subgroups = []
    let current = treatmentRequestDisease

    while (current?.parent) {
        subgroups.push(current)
        current = current.parent
    }

    const group = current

    return {
        group,
        subgroups: subgroups.reverse()
    }
}

export const makeDiseaseTreePathFromArray = (diseases: DiseaseExtended[]) => {
    const diseaseMap = new Map()
    diseases.forEach(disease => diseaseMap.set(disease.id, {...disease, children: []}))

    const nestedStructure: DiseaseExtended[] = []

    diseases.forEach(disease => {
        if (disease.parent?.id) {
            const parent = diseaseMap.get(disease.parent.id)
            if (parent) {
                parent.children.push(diseaseMap.get(disease.id))
            }
        } else {
            nestedStructure.push(diseaseMap.get(disease.id))
        }
    })

    return nestedStructure
}

export const treatmentRequestColumnToLabel = {
    [TreatmentRequestsColumn.enum.fullName]: 'commons:patient',
    [TreatmentRequestsColumn.enum.serviceType]: 'commons:service',
    [TreatmentRequestsColumn.enum.sector]: 'commons:workSector',
    [TreatmentRequestsColumn.enum.status]: 'commons:status',
    [TreatmentRequestsColumn.enum.createdAt]: 'treatment_requests:request_date',
    [TreatmentRequestsColumn.enum.expirationDate]: 'treatment_requests:expiration_date',
    [TreatmentRequestsColumn.enum.acceptedAt]: 'treatment_requests:accepted_date',
    [TreatmentRequestsColumn.enum.rejectedAt]: 'treatment_requests:rejected_date',
    [TreatmentRequestsColumn.enum.rejectedReason]: 'commons:reason',
    [TreatmentRequestsColumn.enum.authorization]: 'commons:authorization',
    [TreatmentRequestsColumn.enum.authorizationDate]: 'commons:authorization_date',
    [TreatmentRequestsColumn.enum.referringDoctorId]: 'treatment_requests:project_referring_doctor',
    [TreatmentRequestsColumn.enum.therapistId]: 'commons:therapist',
    [TreatmentRequestsColumn.enum.diagnosisId]: 'commons:diagnosis',
    [TreatmentRequestsColumn.enum.type]: 'treatment_requests:request_type',
    [TreatmentRequestsColumn.enum.score]: 'commons:score',
    [TreatmentRequestsColumn.enum.residenceAslDistrictId]: 'commons:residence_asl'
} as const satisfies Record<Exclude<TreatmentRequestsColumn, 'select' | 'moreActions'>, TKey>

export const treatmentRequestTableToHiddenColumns = {
    all: [
        TreatmentRequestsColumn.enum.authorization,
        TreatmentRequestsColumn.enum.authorizationDate,
        TreatmentRequestsColumn.enum.acceptedAt,
        TreatmentRequestsColumn.enum.expirationDate,
        TreatmentRequestsColumn.enum.rejectedAt,
        TreatmentRequestsColumn.enum.rejectedReason
    ],
    accepted: [
        TreatmentRequestsColumn.enum.status,
        TreatmentRequestsColumn.enum.authorization,
        TreatmentRequestsColumn.enum.authorizationDate,
        TreatmentRequestsColumn.enum.expirationDate,
        TreatmentRequestsColumn.enum.rejectedAt,
        TreatmentRequestsColumn.enum.rejectedReason,
        TreatmentRequestsColumn.enum.referringDoctorId,
        TreatmentRequestsColumn.enum.therapistId
    ],
    in_progress: [
        TreatmentRequestsColumn.enum.status,
        TreatmentRequestsColumn.enum.createdAt,
        TreatmentRequestsColumn.enum.acceptedAt,
        TreatmentRequestsColumn.enum.expirationDate,
        TreatmentRequestsColumn.enum.rejectedAt,
        TreatmentRequestsColumn.enum.diagnosisId,
        TreatmentRequestsColumn.enum.rejectedReason,
        TreatmentRequestsColumn.enum.score
    ],
    in_review: [
        TreatmentRequestsColumn.enum.status,
        TreatmentRequestsColumn.enum.acceptedAt,
        TreatmentRequestsColumn.enum.expirationDate,
        TreatmentRequestsColumn.enum.rejectedAt,
        TreatmentRequestsColumn.enum.rejectedReason,
        TreatmentRequestsColumn.enum.referringDoctorId,
        TreatmentRequestsColumn.enum.therapistId
    ],
    rejected: [
        TreatmentRequestsColumn.enum.status,
        TreatmentRequestsColumn.enum.acceptedAt,
        TreatmentRequestsColumn.enum.expirationDate,
        TreatmentRequestsColumn.enum.authorization,
        TreatmentRequestsColumn.enum.authorizationDate,
        TreatmentRequestsColumn.enum.referringDoctorId,
        TreatmentRequestsColumn.enum.therapistId
    ],
    waiting_list: [
        TreatmentRequestsColumn.enum.status,
        TreatmentRequestsColumn.enum.acceptedAt,
        TreatmentRequestsColumn.enum.rejectedAt,
        TreatmentRequestsColumn.enum.rejectedReason,
        TreatmentRequestsColumn.enum.referringDoctorId,
        TreatmentRequestsColumn.enum.therapistId
    ]
} as const satisfies Record<Exclude<TreatmentRequestSmall['status'], 'deprecated'> | 'all', TreatmentRequestsColumn[]>

export const useTreatmentRequestSearchParams = () => useParsedSearchParams(TreatmentRequestsSearchParams)
